import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { NAVIGATION_CONFIG, flattenNavigation } from '~components/Sidebar/Sidebar.config';
import { NavItem } from '~components/Sidebar/Sidebar.types';
import { Icon } from '~components/shared/Icon';
import {
  Overlay,
  PaletteContainer,
  SearchInput,
  ResultsList,
  ResultItem,
  ResultIcon,
  ResultLabel,
  ResultShortcut,
  Footer,
  Kbd,
  NoResults,
  SearchIcon,
} from './styles';

function fuzzyMatch(query: string, text: string): boolean {
  const queryLower = query.toLowerCase();
  const textLower = text.toLowerCase();

  if (textLower.includes(queryLower)) return true;

  let queryIndex = 0;
  for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIndex]) {
      queryIndex++;
    }
  }

  return queryIndex === queryLower.length;
}

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { push } = useHistory();

  const allItems = useMemo(() => {
    return flattenNavigation(NAVIGATION_CONFIG).filter(item => item.path);
  }, []);

  const mainModules = useMemo(() => {
    const mainIds = ['dashboard', 'commercial', 'finances', 'marketing', 'store', 'motor', 'register', 'support'];
    return NAVIGATION_CONFIG.filter(item => mainIds.includes(item.id) && item.path);
  }, []);

  const results = useMemo(() => {
    if (!query.trim()) {
      return mainModules;
    }

    return allItems
      .filter(item => fuzzyMatch(query, item.label))
      .slice(0, 10);
  }, [query, allItems, mainModules]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setQuery('');
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % results.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]?.path) {
            push(results[selectedIndex].path!);
            setIsOpen(false);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    },
    [results, selectedIndex, push]
  );

  const handleItemClick = useCallback(
    (item: NavItem) => {
      if (item.path) {
        push(item.path);
        setIsOpen(false);
      }
    },
    [push]
  );

  const handleOverlayClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <PaletteContainer
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="Busca rapida"
        aria-modal="true"
      >
        <SearchIcon>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </SearchIcon>
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder="Pesquise por menu"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Buscar"
          aria-activedescendant={results[selectedIndex]?.id}
        />

        <ResultsList role="listbox" aria-label="Resultados da busca">
          {results.length > 0 ? (
            results.map((item, index) => (
              <ResultItem
                key={item.id}
                id={item.id}
                role="option"
                aria-selected={index === selectedIndex}
                $selected={index === selectedIndex}
                onClick={() => handleItemClick(item)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <ResultIcon>
                  <Icon name={item.icon} size="md" />
                </ResultIcon>
                <ResultLabel>{item.label}</ResultLabel>
                {item.shortcut && <ResultShortcut>{item.shortcut}</ResultShortcut>}
              </ResultItem>
            ))
          ) : (
            <NoResults>Nenhum resultado encontrado</NoResults>
          )}
        </ResultsList>

        <Footer>
          <span>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            para navegar
          </span>
          <span>
            <Kbd>Enter</Kbd>
            para selecionar
          </span>
          <span>
            <Kbd>Esc</Kbd>
            para fechar
          </span>
        </Footer>
      </PaletteContainer>
    </Overlay>
  );
}
